//@ts-nocheck
import React, { useEffect, useState } from "react";

import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";

import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

import logo from "../../assets/images/logo.png";
import SoftBox from "../common/softBox";
import SoftTypography from "../common/softTypography";
import SidenavCollapse from "./SidenavCollapse";
import SidenavRoot from "./SidenavRoot";
import sidenavLogoLabel from "./styles/sidenav";
import { useSoftUIController, setMiniSidenav } from "../../context";
import { Collapse } from "@mui/material";
import { MdExpandMore, MdExpandLess } from "react-icons/md";

// Route item type
type RouteType = {
  type: "collapse" | "title" | "divider";
  name?: string;
  key: string;
  icon?: JSX.Element | string;
  title?: string;
  noCollapse?: boolean;
  route?: string;
  href?: string;
};

// Props
interface SidenavProps {
  color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark" | string;
  brand?: string;
  brandName: string;
  routes: RouteType[];
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const Sidenav: React.FC<SidenavProps> = ({
  color = "primary",
  brand,
  brandName,
  routes,
  ...rest
}) => {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentSidenav } = controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];
  const normalizeKey = (value?: string) =>
    !value ? "" : value.endsWith("s") ? value : `${value}s`;
  const isRouteActive = (routeKey: string) =>
    routeKey === collapseName || normalizeKey(routeKey) === normalizeKey(collapseName);
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    const handleMiniSidenav = () => {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    };

    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch]);

  useEffect(() => {
    let currentTitle: string | null = null;
    for (const route of routes) {
      if (route.type === "title") {
        currentTitle = route.key;
      } else if (route.type === "collapse" && isRouteActive(route.key)) {
        if (currentTitle) setOpenGroup(currentTitle);
        break;
      }
    }
  }, [pathname]);

  const toggleGroup = (key: string) => {
    setOpenGroup(prev => (prev === key ? null : key)); // toggle current or close others
  };

  const renderGroupedRoutes = () => {
    const groups: { key: string; title: JSX.Element; items: JSX.Element[] }[] = [];
    let currentGroup: { key: string; title: JSX.Element; items: JSX.Element[] } | null = null;

    routes.forEach((route) => {
      if (route.type === "title" && route.title) {
        if (currentGroup) {
          groups.push(currentGroup);
        }
        currentGroup = {
          key: route.key,
          title: (
            <SoftTypography
              key={route.key}
              display="block"
              variant="caption"
              fontWeight="bold"
              textTransform="uppercase"
              opacity={0.6}
              pl={3}
              mt={2}
              mb={1}
              ml={1}
            >
              {route.title}
            </SoftTypography>
          ),
          items: [],
        };
      } else if (route.type === "collapse" && route.name) {
        const content = route.href ? (
          <Link
            href={route.href}
            key={route.key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              color={color}
              name={route.name}
              icon={route.icon}
              active={isRouteActive(route.key)}
              noCollapse={route.noCollapse}
            />
          </Link>
        ) : (
          <NavLink to={route.route || "#"} key={route.key}>
            <SidenavCollapse
              color={color}
              name={route.name}
              icon={route.icon}
              active={isRouteActive(route.key)}
              noCollapse={route.noCollapse}
            />
          </NavLink>
        );

        if (currentGroup) {
          currentGroup.items.push(content);
        } else {
          groups.push({
            key: route.key,
            title: null, // no group title
            items: [content],
          });
        }
      } else if (route.type === "divider") {
        groups.push({
          key: route.key,
          title: null,
          items: [<Divider key={route.key} />],
        });
      }
    });

    // Push last group
    if (currentGroup) {
      groups.push(currentGroup);
    }

    return groups.filter((group) => group.items.length > 0).map((group) => {
      const isOpen = openGroup === group.key;

      return (
        <React.Fragment key={group.key}>
          {group.title && (
            <SoftBox
              onClick={() => toggleGroup(group.key)}
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 3,
                py: 1.25,
                bgcolor: isOpen ? "rgba(0,0,0,0.05)" : "transparent",
                "&:hover": {
                  bgcolor: "rgba(0,0,0,0.04)",
                },
              }}
            >
              <SoftTypography
                display="block"
                variant="caption"
                fontWeight="bold"
                textTransform="uppercase"
                opacity={0.8}
              >
                {group.title.props.children}
              </SoftTypography>
              <Icon sx={{ fontSize: "1.2rem" }}>
                {isOpen ? <MdExpandLess /> : <MdExpandMore />}
              </Icon>
            </SoftBox>
          )}

          <Collapse in={group.title ? isOpen : true} timeout="auto" unmountOnExit>
            <List sx={{ pl: group.title ? 2 : 0 }}>{group.items}</List>
          </Collapse>
        </React.Fragment>
      );
    });
  };

  return (
    <SidenavRoot {...rest} variant="permanent" ownerState={{ transparentSidenav, miniSidenav }}>
      <SoftBox>
        <SoftBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <SoftTypography variant="h6" color="dark">
            <Icon>
              <HighlightOffIcon />
            </Icon>
          </SoftTypography>
        </SoftBox>

        <SoftBox
          component={NavLink}
          to="/"
          display="flex"
          alignItems="center"
          bgColor="primary"
          justifyContent={"center"}
        >
          <SoftBox
            textAlign="center"
            width={"100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
            bgColor="primary"
            py={1}
          >
            <SoftTypography textAlign="center" component="h6" variant="button" fontWeight="medium">
              <SoftBox
                textAlign="center"
                component="img"
                src={logo}
                alt={brandName}
                borderRadius="lg"
                bgColor="primary"
              />
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      </SoftBox>

      <Divider />
      <SoftBox component="nav">{renderGroupedRoutes()}</SoftBox>
    </SidenavRoot>
  );
};

Sidenav.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
